import { useEffect, useState } from 'react'
import { useIntlayer } from 'react-intlayer'

type SaveIndicatorProps = {
  isSaving: boolean
  lastSaved: Date | null
  error: string | null
  retryAttempt?: number
  isMobile?: boolean
}

export const SaveIndicator = ({
  isSaving,
  lastSaved,
  error,
  retryAttempt,
  isMobile = false,
}: SaveIndicatorProps) => {
  const { saveFailed, retrying, saving, saved } = useIntlayer('saveIndicator')
  const [showSuccess, setShowSuccess] = useState(false)

  useEffect(() => {
    if (lastSaved && !isSaving && !error) {
      setShowSuccess(true)
      const timer = setTimeout(() => setShowSuccess(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [lastSaved, isSaving, error])

  if (error) {
    return (
      <div
        className={`fixed ${isMobile ? 'top-16 right-4' : 'bottom-4 right-4'} bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm z-[9999]`}
        data-gtm="save-indicator"
      >
        {saveFailed}
        {error}
      </div>
    )
  }

  if (isSaving) {
    return (
      <div
        className={`fixed ${isMobile ? 'top-16 right-4' : 'bottom-4 right-4'} bg-gray-700 text-white px-4 py-2 rounded-lg shadow-lg text-sm flex items-center gap-2 z-[9999]`}
        data-gtm="save-indicator"
      >
        <svg
          className="animate-spin h-4 w-4"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          ></circle>
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          ></path>
        </svg>
        {retryAttempt && retryAttempt > 0 ? `${retrying}${retryAttempt}/3)` : saving}
      </div>
    )
  }

  if (showSuccess) {
    return (
      <div
        className={`fixed ${isMobile ? 'top-16 right-4' : 'bottom-4 right-4'} bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg text-sm flex items-center gap-2 z-[9999]`}
        data-gtm="save-indicator"
      >
        <svg
          className="h-4 w-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M5 13l4 4L19 7"
          ></path>
        </svg>
        {saved}
      </div>
    )
  }

  return null
}
